import { IApplePayPaymentAuthorizationCallbackActions } from "../../models/IApplePayPaymentAuthorizationCallbackActions";
import { RequestJwtUpdateHandler } from "../../../../../application/core/services/st-codec/RequestJwtUpdateHandler";
import { APPLE_PAY_STATUS } from "../session/models/ApplePayStatus";
import { ApplePayErrorUtils } from "./ApplePayErrorUtils";
import { IJwtEnhancedApplePayPaymentAuthorizationResult } from "../../models/IJwtEnhancedApplePayPaymentAuthorizationResult";

export class ApplePayPaymentAuthorizationResolver implements IApplePayPaymentAuthorizationCallbackActions {
  private static readonly timeoutMs = 25000;
  private isResolved = false;
  private timeoutId: number | null = null;
  private authorizationResult?: ApplePayJS.ApplePayPaymentAuthorizationResult;

  constructor(
    private readonly processPayment: (
      result?: ApplePayJS.ApplePayPaymentAuthorizationResult,
    ) => void,
    private readonly jwtRequestHandler?: RequestJwtUpdateHandler,
  ) {
    // Set 25-second timeout
    this.setupTimeout();
  }

  private setupTimeout(): void {
    this.timeoutId = window.setTimeout(() => {
      if (!this.isResolved) {
        console.warn(
          "Apple Pay authorization callback timed out. Proceeding with payment processing.",
        );
        this.processPayment();
        this.isResolved = true;
      }
    }, ApplePayPaymentAuthorizationResolver.timeoutMs);
  }

  private validateStatus(status: number): boolean {
    const validStatuses = [
      APPLE_PAY_STATUS.STATUS_SUCCESS,
      APPLE_PAY_STATUS.STATUS_FAILURE,
    ];
    if (!validStatuses.includes(status)) {
      console.warn(
        `'${status}' is not a recognised status for actions.resolve. Valid values are: ApplePaySession.STATUS_SUCCESS (${APPLE_PAY_STATUS.STATUS_SUCCESS}), ApplePaySession.STATUS_FAILURE (${APPLE_PAY_STATUS.STATUS_FAILURE})`,
      );
      return false;
    }
    return true;
  }

  public resolve(param?: IJwtEnhancedApplePayPaymentAuthorizationResult): void {
    if (this.isResolved) {
      return; // Already resolved or timed out
    }

    this.isResolved = true;

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (param?.jwt && this.jwtRequestHandler) {
      this.jwtRequestHandler.request(param.jwt);
    }

    if (param?.status !== undefined) {
      if (!this.validateStatus(param.status)) {
        // Invalid status - close wallet and fail payment
        this.authorizationResult = {
          status: APPLE_PAY_STATUS.STATUS_FAILURE,
        };
        this.processPayment(this.authorizationResult);
        return;
      }
      // Create authorization result without jwt property
      this.authorizationResult = {
        status: param.status,
        ...(param.errors && {
          errors:
            param.errors.length > 0 &&
            ApplePayErrorUtils.isApplePayErrorAvailable()
              ? ApplePayErrorUtils.setApplePayErrorForAppleDevice(param.errors)
              : param.errors,
        }),
        ...(param.orderDetails && { orderDetails: param.orderDetails }),
      };
    }

    this.processPayment(this.authorizationResult);
  }
}
